import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Btn, Modal } from "./UI";
import { FSelect } from "../pages/engagement/FormControls";
import { UploadCloud, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const OUTCOMES = [
  { key: "new", label: "New", tone: "bg-[#E8F5E9] text-[#2E7D32]" },
  { key: "update", label: "Update", tone: "bg-[#E3F2FD] text-[#1565C0]" },
  { key: "unchanged", label: "Unchanged", tone: "bg-[#F5F2EB] text-[#6D4C41]" },
  { key: "duplicate", label: "Duplicate", tone: "bg-[#FFF3E0] text-[#8D5A00]" },
  { key: "invalid", label: "Invalid", tone: "bg-[#FFEBEE] text-[#C62828]" },
];
const toneOf = (k) => OUTCOMES.find((o) => o.key === k)?.tone || "";

function MappingStep({ headers, mapping, setMapping, fields }) {
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="import-mapping">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="mb-1 block text-xs font-bold uppercase text-[#7B6157]">{f.label}{f.required ? " *" : ""}</label>
          <FSelect data-testid={`import-map-${f.key}`} value={mapping[f.key] || ""} placeholder="Not in file"
            onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}>
            {headers.map((h) => <option key={h} value={h}>{h}</option>)}
          </FSelect>
        </div>
      ))}
    </div>
  );
}

function PreviewRows({ batchId, outcome, columns }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [outcome]);
  useEffect(() => {
    api.get(`/library-imports/${batchId}/rows`, { params: { outcome: outcome || undefined, page, size: 25 } })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); }).catch((e) => toast.error(apiError(e)));
  }, [batchId, outcome, page]);
  const pages = Math.max(1, Math.ceil(total / 25));
  return (
    <div className="rounded-xl border border-[#EFEBE0]">
      <div className="max-h-72 overflow-auto">
        <table className="min-w-full text-xs" data-testid="import-preview-table">
          <thead className="sticky top-0 bg-[#FDFCF8] text-left text-[10px] font-bold uppercase text-[#7B6157]">
            <tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">Status</th>{columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}<th className="px-3 py-2">Detail</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#F1EEE7]" data-testid={`import-row-${r.row_num}`}>
                <td className="px-3 py-1.5 text-[#7B6157]">{r.row_num}</td>
                <td className="px-3 py-1.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${toneOf(r.outcome)}`}>{r.outcome}</span></td>
                {columns.map((c) => <td key={c.key} className="px-3 py-1.5 text-[#3E2723]">{c.render ? c.render(r.data) : r.data?.[c.key] || "-"}</td>)}
                <td className="px-3 py-1.5 text-[#6D4C41]">{r.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-[#F1EEE7] px-3 py-2 text-[11px] text-[#7B6157]">
        <span>{total} row{total === 1 ? "" : "s"}</span>
        <div className="flex gap-1">
          <Btn variant="ghost" className="!px-2 !py-1 text-xs" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="px-2 py-1">Page {page} of {pages}</span>
          <Btn variant="ghost" className="!px-2 !py-1 text-xs" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ result, entity }) {
  const lines = [["Rows in file", result.source_rows], ["Added", result.inserted], ["Updated", result.updated], ["Unchanged", result.unchanged],
    ["Skipped as invalid", result.skipped_invalid], ["Skipped as duplicate", result.skipped_duplicate], ["Write conflicts", result.write_conflicts ?? 0], ["Starter samples archived", result.archived_samples],
    [`Active ${entity} entries now`, result.catalog_active_total]];
  return (
    <div className="space-y-3" data-testid="import-result">
      <div className={`flex items-center gap-2 rounded-xl p-3 text-sm font-semibold ${result.reconciled ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFEBEE] text-[#C62828]"}`} data-testid="import-reconciled">
        {result.reconciled ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {result.reconciled ? "Row count reconciled against the source file" : "Row count does not reconcile. Review the import history."}
      </div>
      <dl className="grid grid-cols-2 gap-2">
        {lines.map(([k, v]) => <div key={k} className="rounded-lg bg-[#FDFCF8] px-3 py-2"><dt className="text-[10px] uppercase text-[#7B6157]">{k}</dt><dd className="font-heading text-lg font-bold text-[#3E2723]">{v}</dd></div>)}
      </dl>
    </div>
  );
}

/** Generic upload, map, dry-run preview, and commit flow for library imports. */
export function LibraryImportWizard({ open, onClose, onDone, entity, previewUrl, commitUrl, fields, columns, templateCsv }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [outcome, setOutcome] = useState("");
  const [archiveSamples, setArchiveSamples] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); setMapping({}); setOutcome(""); setResult(null); } }, [open]);

  const runPreview = async (f = file, mp = null) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (mp) fd.append("mapping", JSON.stringify(mp));
    try { const r = await api.post(previewUrl, fd); setPreview(r.data); setMapping(r.data.mapping || {}); setOutcome(""); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true);
    try { const r = await api.post(commitUrl(preview.id), { archive_missing_samples: archiveSamples }); setResult(r.data); toast.success("Import committed"); onDone?.(); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([templateCsv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${entity.toLowerCase()}-import-template.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const c = preview?.counts;

  return (
    <Modal open={open} onClose={onClose} title={`Import ${entity} file`} wide>
      {result ? <ResultCard result={result} entity={entity.toLowerCase()} /> : (
        <div className="space-y-4" data-testid="import-wizard">
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[#D7CCC8] bg-[#FDFCF8] p-5 text-center hover:border-[#8D6E63]">
            {file ? <FileSpreadsheet size={22} className="text-[#4E342E]" /> : <UploadCloud size={22} className="text-[#7B6157]" />}
            <span className="text-sm font-semibold text-[#3E2723]">{file ? file.name : "Choose an Excel (.xlsx) or CSV file"}</span>
            <span className="text-[11px] text-[#7B6157]">Nothing is saved until you review the dry-run preview and commit.</span>
            <input type="file" accept=".xlsx,.xlsm,.csv,.tsv,.txt" className="hidden" data-testid="import-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); setPreview(null); if (f) runPreview(f); }} />
          </label>
          <button type="button" onClick={downloadTemplate} data-testid="import-template-btn" className="text-xs font-semibold text-[#6D4C41] underline">Download a blank template</button>
          {busy && <div className="text-xs text-[#7B6157]" data-testid="import-busy">Reading and validating every row…</div>}
          {preview && (
            <>
              <MappingStep headers={preview.headers} mapping={mapping} setMapping={setMapping} fields={fields} />
              <Btn variant="outline" data-testid="import-remap-btn" disabled={busy} onClick={() => runPreview(file, mapping)}>Re-run preview with this mapping</Btn>
              {preview.needs_mapping ? (
                <div className="rounded-xl bg-[#FFF3E0] p-3 text-xs text-[#8D5A00]" data-testid="import-needs-mapping">Pick which columns hold the required fields, then re-run the preview. {preview.total_rows} data rows found.</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2" data-testid="import-counts">
                    <button type="button" onClick={() => setOutcome("")} className={`rounded-full px-3 py-1 text-xs font-bold ${outcome === "" ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#4E342E]"}`} data-testid="import-count-total">All {c.total}</button>
                    {OUTCOMES.map((o) => (
                      <button key={o.key} type="button" onClick={() => setOutcome(o.key)} data-testid={`import-count-${o.key}`}
                        className={`rounded-full px-3 py-1 text-xs font-bold ${outcome === o.key ? "ring-2 ring-[#4E342E]" : ""} ${o.tone}`}>{o.label} {c[o.key]}</button>
                    ))}
                  </div>
                  <PreviewRows batchId={preview.id} outcome={outcome} columns={columns} />
                  {preview.samples_not_in_file > 0 && (
                    <label className="flex items-start gap-2 rounded-xl bg-[#FDFCF8] p-3 text-xs text-[#5D4037]">
                      <input type="checkbox" data-testid="import-archive-samples" checked={archiveSamples} onChange={(e) => setArchiveSamples(e.target.checked)} className="mt-0.5" />
                      Archive the {preview.samples_not_in_file} starter sample entries that are not in this file, so only your list stays active.
                    </label>
                  )}
                  <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn data-testid="import-commit-btn" disabled={busy || (c.new + c.update) === 0} onClick={commit}>
                      {busy ? "Committing…" : `Commit ${c.new + c.update} change${c.new + c.update === 1 ? "" : "s"}`}
                    </Btn>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
